import { ColumnsType } from 'antd/lib/table';
import { useMemo } from 'react';
import { formatToDate } from 'utils/formatToDate';

import { DocumentModel } from 'api/documents/documents.model';
import TableCellWithPublicationStatus from 'components/TableCellWithPublicationStatus';
import { useAuthenticatedContext } from 'hooks/useAuthenticated';
import useCertificateHelper from 'hooks/useCertificateHelper';
import { CertificateDetailsModel } from 'models/certificate.model';
import { ADDITIONAL_DETAILS, NESTED_DETAILS } from 'models/certificate.model';
import { COMPANY } from 'models/company.model';
import { CATEGORY_TYPE } from 'models/documents.model';

import { BatteryPacksPageTabsEnum } from '../../constants';

interface ConfigInterface {
  creationEnabledByTabs: Record<string, string>;
  categoryByTabs: Record<string, CATEGORY_TYPE>;
  configByTabs: Record<string, ColumnsType<DocumentModel<CertificateDetailsModel>>>;
}

const useTableConfig = () => {
  const { currentUser } = useAuthenticatedContext();
  const { getLabel } = useCertificateHelper();

  const config = useMemo<ConfigInterface>(() => {
    switch (currentUser?.company) {
      case COMPANY.BATTERY_PACKS:
      default:
        return {
          creationEnabledByTabs: {
            [BatteryPacksPageTabsEnum.RECEIVING_CONFIRMATION]: 'purchasing-departments',
          },
          categoryByTabs: {
            [BatteryPacksPageTabsEnum.SHIPPING_CONFIRMATION]:
              CATEGORY_TYPE.BATTERY_CELLS_SHIPPING_CONFIRMATION,
            [BatteryPacksPageTabsEnum.RECEIVING_CONFIRMATION]:
              CATEGORY_TYPE.BATTERY_CELLS_RECEIVING_CONFIRMATION,
          },
          configByTabs: {
            [BatteryPacksPageTabsEnum.SHIPPING_CONFIRMATION]: [
              {
                title: getLabel(ADDITIONAL_DETAILS.ISSUER),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.ISSUER],
                render: TableCellWithPublicationStatus,
              },
              {
                title: getLabel(NESTED_DETAILS.BATTERY_CELL_MODEL),
                dataIndex: ['additionalDetails', NESTED_DETAILS.BATTERY_CELL_MODEL],
              },
              {
                title: getLabel(NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER),
                dataIndex: ['additionalDetails', NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER],
              },
              {
                title: getLabel(ADDITIONAL_DETAILS.SHIPPING_DATE),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.SHIPPING_DATE],
                render: formatToDate,
              },
            ],
            [BatteryPacksPageTabsEnum.RECEIVING_CONFIRMATION]: [
              {
                title: getLabel(ADDITIONAL_DETAILS.RECIPIENT),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.RECIPIENT],
                render: TableCellWithPublicationStatus,
              },
              {
                title: getLabel(NESTED_DETAILS.BATTERY_CELL_MODEL),
                dataIndex: ['additionalDetails', NESTED_DETAILS.BATTERY_CELL_MODEL],
              },
              {
                title: getLabel(NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER),
                dataIndex: ['additionalDetails', NESTED_DETAILS.BATTERY_CELLS_BATCH_NUMBER],
              },
              {
                title: getLabel(ADDITIONAL_DETAILS.RECEIVING_DATE),
                dataIndex: ['additionalDetails', ADDITIONAL_DETAILS.RECEIVING_DATE],
                render: formatToDate,
              },
            ],
          },
        };
    }
  }, [currentUser?.company, getLabel]);

  return config;
};

export default useTableConfig;
